import { useCallback } from "react";
import { api } from "../utils/apiHandler";
import type {
  User,
  CreateUserRequest,
  CreateUserResponse,
  UpdateUserRequest,
  UpdateUserResponse,
  CheckHandleResponse,
  FindByHandleResponse,
} from "./types/usersTypes";
import type { AxiosResponse } from "axios";

const USERS_PATH = "/users";

const useUsersResource = () => {
  // Get user by ID
  const getUser = useCallback(async (id: string): Promise<User> => {
    const response: AxiosResponse<User> = await api.request<
      undefined,
      AxiosResponse<User>
    >({
      method: "GET",
      url: `${USERS_PATH}/${id}`,
    });
    return response.data;
  }, []);

  // Create user
  const createUser = useCallback(
    async (params: CreateUserRequest): Promise<CreateUserResponse> => {
      const response: AxiosResponse<CreateUserResponse> = await api.request<
        CreateUserRequest,
        AxiosResponse<CreateUserResponse>
      >({
        method: "POST",
        url: USERS_PATH,
        data: params,
      });
      return response.data;
    },
    []
  );

  // Update user
  const updateUser = useCallback(
    async (
      id: string,
      params: UpdateUserRequest
    ): Promise<UpdateUserResponse> => {
      const response: AxiosResponse<UpdateUserResponse> = await api.request<
        UpdateUserRequest,
        AxiosResponse<UpdateUserResponse>
      >({
        method: "PATCH",
        url: `${USERS_PATH}/${id}`,
        data: params,
      });
      return response.data;
    },
    []
  );

  // Delete user
  const deleteUser = useCallback(async (id: string): Promise<void> => {
    await api.request({
      method: "DELETE",
      url: `${USERS_PATH}/${id}`,
    });
  }, []);

  // Search handle
  const searchHandle = useCallback(async (handle: string): Promise<User[]> => {
    const response: AxiosResponse<User[]> = await api.request<
      undefined,
      AxiosResponse<User[]>
    >({
      method: "GET",
      url: `${USERS_PATH}/search_handle`,
      params: { handle },
    });
    return response.data;
  }, []);

  // Check handle
  const checkHandle = useCallback(
    async (handle: string): Promise<CheckHandleResponse> => {
      const response: AxiosResponse<CheckHandleResponse> = await api.request<
        undefined,
        AxiosResponse<CheckHandleResponse>
      >({
        method: "GET",
        url: `${USERS_PATH}/check_handle`,
        params: { handle },
      });
      return response.data;
    },
    []
  );

  // Find by handle
  const findByHandle = useCallback(
    async (handle: string): Promise<FindByHandleResponse> => {
      const response: AxiosResponse<FindByHandleResponse> = await api.request<
        undefined,
        AxiosResponse<FindByHandleResponse>
      >({
        method: "GET",
        url: `${USERS_PATH}/find_by_handle`,
        params: { handle },
      });
      return response.data;
    },
    []
  );

  // Universe match - find random user
  const universeMatch = useCallback(async (): Promise<User> => {
    const response: AxiosResponse<User> = await api.request<
      undefined,
      AxiosResponse<User>
    >({
      method: "GET",
      url: `${USERS_PATH}/universe_find_match`,
    });
    return response.data;
  }, []);

  // Get random matches for search page
  const randomMatches = useCallback(async (): Promise<User[]> => {
    const response: AxiosResponse<User[]> = await api.request<
      undefined,
      AxiosResponse<User[]>
    >({
      method: "GET",
      url: `${USERS_PATH}/random_matches`,
    });
    return response.data;
  }, []);

  return {
    getUser,
    createUser,
    updateUser,
    deleteUser,
    searchHandle,
    checkHandle,
    findByHandle,
    universeMatch,
    randomMatches,
  };
};

export default useUsersResource;
